/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col, Form, Row, Select, Checkbox, Button } from 'antd'
import { FC, useEffect } from 'react'
import { CustomFormInstance } from '../../../../types'
import {
    MachineForm,
    MachineNames,
    MachineValues,
    SharedQuestionProps,
} from '../types'
import { ValidQuestionKeys } from '../../../../hooks/useGerateQuestions'
import {
    StyledSpan,
    StyledFormItem,
    StyledInput,
    StyledInputOptional,
} from '../methods.styles'
import { connectionOptions, connectionRelation } from '../../../../const'
import { extractComponentName } from '../../../functions'

type Props = SharedQuestionProps & {
    form: CustomFormInstance<MachineForm>
}

const DependantComponentConnections: FC<Props> = ({
    form,
    setContentBasedItemsToReview,
}) => {
    const dependantConnections = form.getFieldValue<
        MachineForm['dependantComponentsConnections']
    >('dependantComponentsConnections')

    useEffect(() => {
        if (!setContentBasedItemsToReview) return

        const hasIncompleteConnection =
            !dependantConnections ||
            dependantConnections.length === 0 ||
            dependantConnections.some((conn: any) => {
                if (!conn) return true
                const has1st = !!conn['1stComponent']
                const has2nd = !!conn['2ndComponent']

                if (conn.notSet) {
                    return !has1st || !has2nd || !conn['2ndPart']
                }

                return !has1st || !has2nd || !conn.connectionType
            })

        setContentBasedItemsToReview((prev) => {
            const key =
                MachineNames.dependantComponentsConnections as ValidQuestionKeys
            if (hasIncompleteConnection) {
                if (!prev.includes(key)) return [...prev, key]
                return prev
            }

            return prev.filter((item) => item !== key)
        })
    }, [dependantConnections, setContentBasedItemsToReview])

    const handleConnectionTypeChange = (field: any) => () => {
        const name = 'dependantComponentsConnections'
        const formValue: any =
            form.getFieldValue<MachineForm['dependantComponentsConnections']>(
                name
            )
        const updateFormValue = formValue.map((value: any, index: number) => {
            if (index === field.key) {
                value.subConnectionType = ''
            }
            return value
        })
        form.setFieldsValue({ [name]: updateFormValue })
    }

    /*
        components from partsOfIndependentClaim (parent claim 1).
        include all components from partsOfDependentClaim (child claim components),
        EXCEPT exclude the direct parent component(s) of the currently selected 1st component.
     */
    const getSecondComponentOptions = (field: any) => {
        const partsOfIndependentClaim = form.getFieldValue<
            MachineValues['partsOfIndependentClaim']
        >('partsOfIndependentClaim')

        const partsOfDependentClaim = form.getFieldValue<
            MachineValues['partsOfDependentClaim']
        >('partsOfDependentClaim')

        // Get the currently selected 1st component for this row
        const connections = form.getFieldValue<
            MachineForm['dependantComponentsConnections']
        >('dependantComponentsConnections')
        const selected1stComponent = connections?.[field.name]?.['1stComponent']

        let directParentIds: string[] = []
        if (selected1stComponent && selected1stComponent.includes('+=>')) {
            directParentIds = [selected1stComponent.split('+=>')[0]]
        }

        const allCandidateIds = Array.from(
            new Set([
                ...(partsOfIndependentClaim ?? []),
                ...(partsOfDependentClaim ?? []).filter((x: string) => !!x),
            ])
        )

        const excludedIds = new Set([selected1stComponent, ...directParentIds])

        const filteredIds = allCandidateIds.filter(
            (id: string) => !excludedIds.has(id)
        )

        return filteredIds.map((id: string) => ({
            value: id,
            label: extractComponentName(id, form),
        }))
    }

    return (
        <Form.List name="dependantComponentsConnections">
            {(fields, { add }) => {
                // const partsOfIndependentClaim = form.getFieldValue<
                //     MachineValues['partsOfIndependentClaim']
                // >('partsOfIndependentClaim')

                const partsOfDependentClaim = form.getFieldValue<
                    MachineValues['partsOfDependentClaim']
                >('partsOfDependentClaim')
                // const formatedIndependentOptions = partsOfIndependentClaim.map(
                //     (item: string) => {
                //         return extractComponentName(item, form)
                //     }
                // )
                const formatedDependentOptions = partsOfDependentClaim
                    .filter((x: string) => !!x)
                    .map((item: string) => {
                        return extractComponentName(item, form)
                    })
                const connectionTypes = form.getFieldValue<
                    MachineForm['dependantComponentsConnections']
                >('dependantComponentsConnections')
                return (
                    <>
                        {fields.map((field) => (
                            <Form.Item style={{ marginBottom: '24px' }}>
                                <Row gutter={[12, 0]}>
                                    <Col span={12}>
                                        <Form.Item noStyle shouldUpdate>
                                            {() => (
                                                <Form.Item
                                                    {...field}
                                                    label="1st component"
                                                    name={[
                                                        field.name,
                                                        '1stComponent',
                                                    ]}
                                                    fieldKey={[
                                                        field.fieldKey as number,
                                                        '1stComponent',
                                                    ]}
                                                    hasFeedback
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                '1st component is required',
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        options={formatedDependentOptions.map(
                                                            (
                                                                item: string,
                                                                index: number
                                                            ) => ({
                                                                value: partsOfDependentClaim[
                                                                    index
                                                                ],
                                                                label: item,
                                                            })
                                                        )}
                                                    />
                                                </Form.Item>
                                            )}
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item noStyle shouldUpdate>
                                            {() => (
                                                <Form.Item
                                                    {...field}
                                                    label="2nd component"
                                                    name={[
                                                        field.name,
                                                        '2ndComponent',
                                                    ]}
                                                    fieldKey={[
                                                        field.fieldKey as number,
                                                        '2ndComponent',
                                                    ]}
                                                    hasFeedback
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                '2nd component is required',
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        // options={formatedIndependentOptions.map(
                                                        //         (
                                                        //             item: string,
                                                        //             index: number
                                                        //         ) => ({
                                                        //             value: partsOfIndependentClaim[
                                                        //                 index
                                                        //             ],
                                                        //             label: item,
                                                        //         })

                                                        options={getSecondComponentOptions(
                                                            field
                                                        )}
                                                    />
                                                </Form.Item>
                                            )}
                                        </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                        <Form.Item
                                            name={[field.name, 'notSet']}
                                            valuePropName="checked"
                                            style={{ marginBottom: '20px' }}
                                        >
                                            <Checkbox>
                                                I want to describe the
                                                connection
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    {!form.getFieldValue<
                                        MachineForm['dependantComponentsConnections']
                                    >('dependantComponentsConnections')[
                                        field.name
                                    ]?.notSet ? (
                                        <Col span={24}>
                                            <Form.Item noStyle shouldUpdate>
                                                {() => (
                                                    <Form.Item
                                                        {...field}
                                                        label="Connection type"
                                                        name={[
                                                            field.name,
                                                            'connectionType',
                                                        ]}
                                                        fieldKey={[
                                                            field.fieldKey as number,
                                                            'connectionType',
                                                        ]}
                                                        hasFeedback
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message:
                                                                    'Connection type is required',
                                                            },
                                                        ]}
                                                    >
                                                        <Select
                                                            onChange={handleConnectionTypeChange(
                                                                field
                                                            )}
                                                            options={
                                                                connectionOptions
                                                            }
                                                        />
                                                    </Form.Item>
                                                )}
                                            </Form.Item>
                                            {connectionRelation[
                                                connectionTypes[field.name]
                                                    ?.connectionType
                                            ] ? (
                                                <Form.Item noStyle shouldUpdate>
                                                    {() => (
                                                        <Form.Item
                                                            {...field}
                                                            label=""
                                                            name={[
                                                                field.name,
                                                                'subConnectionType',
                                                            ]}
                                                            fieldKey={[
                                                                field.fieldKey as number,
                                                                'subConnectionType',
                                                            ]}
                                                            // hasFeedback
                                                        >
                                                            <Select
                                                                options={
                                                                    connectionRelation[
                                                                        connectionTypes[
                                                                            field
                                                                                .name
                                                                        ]
                                                                            ?.connectionType
                                                                    ]
                                                                }
                                                            />
                                                        </Form.Item>
                                                    )}
                                                </Form.Item>
                                            ) : null}
                                        </Col>
                                    ) : (
                                        <Col span={24}>
                                            <Form.Item
                                                noStyle
                                                name={[
                                                    field.name,
                                                    'additionalConnectionInfo',
                                                ]}
                                                rules={[
                                                    {
                                                        validator: async () => {
                                                            const dependantComponentsConnections =
                                                                form.getFieldValue<
                                                                    MachineForm['dependantComponentsConnections']
                                                                >(
                                                                    'dependantComponentsConnections'
                                                                )[field.key]
                                                            if (
                                                                dependantComponentsConnections &&
                                                                dependantComponentsConnections.notSet
                                                            ) {
                                                                const errorList =
                                                                    []
                                                                if (
                                                                    dependantComponentsConnections[
                                                                        '2ndPart'
                                                                    ] == null ||
                                                                    dependantComponentsConnections[
                                                                        '2ndPart'
                                                                    ] === ''
                                                                ) {
                                                                    errorList.push(
                                                                        ' 2nd part of description about connectivity is required'
                                                                    )
                                                                }
                                                                if (
                                                                    errorList.length >
                                                                    0
                                                                ) {
                                                                    throw new Error(
                                                                        errorList.toString()
                                                                    )
                                                                }
                                                                // throw new Error(
                                                                //     'Description about connectivity is required'
                                                                // )
                                                            }
                                                        },
                                                    },
                                                ]}
                                            >
                                                <StyledFormItem
                                                    name={[
                                                        field.name,
                                                        '1stPart',
                                                    ]}
                                                >
                                                    <StyledInputOptional
                                                        type="text"
                                                        style={{
                                                            width: '150px',
                                                        }}
                                                    />
                                                </StyledFormItem>
                                                <StyledSpan>
                                                    the{' '}
                                                    {extractComponentName(
                                                        form.getFieldValue<
                                                            MachineForm['dependantComponentsConnections']
                                                        >(
                                                            'dependantComponentsConnections'
                                                        )[field.key]?.[
                                                            '1stComponent'
                                                        ],
                                                        form
                                                    ) ?? '1st Component'}
                                                </StyledSpan>
                                                <StyledFormItem
                                                    name={[
                                                        field.name,
                                                        '2ndPart',
                                                    ]}
                                                >
                                                    <StyledInput
                                                        type="text"
                                                        style={{
                                                            width: '150px',
                                                        }}
                                                    />
                                                </StyledFormItem>
                                                <StyledSpan>
                                                    the{' '}
                                                    {extractComponentName(
                                                        form.getFieldValue<
                                                            MachineForm['dependantComponentsConnections']
                                                        >(
                                                            'dependantComponentsConnections'
                                                        )[field.key]?.[
                                                            '2ndComponent'
                                                        ],
                                                        form
                                                    ) ?? '2nd Component'}
                                                </StyledSpan>
                                                <StyledFormItem
                                                    name={[
                                                        field.name,
                                                        '3rdPart',
                                                    ]}
                                                >
                                                    <StyledInputOptional
                                                        type="text"
                                                        style={{
                                                            width: '150px',
                                                        }}
                                                    />
                                                </StyledFormItem>
                                            </Form.Item>
                                        </Col>
                                    )}
                                </Row>
                            </Form.Item>
                        ))}
                        <Form.Item>
                            <Button type="dashed" onClick={() => add()} block>
                                Add connection
                            </Button>
                        </Form.Item>
                    </>
                )
            }}
        </Form.List>
    )
}

export default DependantComponentConnections
